/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import BulmaCard from 'react-bulma-components/lib/components/card';
import BulmaColumns from 'react-bulma-components/lib/components/columns';
import BulmaContainer from 'react-bulma-components/lib/components/container';
import BulmaHero from 'react-bulma-components/lib/components/hero';

import { css } from '@emotion/core';

import { RichText } from 'prismic-reactjs';
import { linkResolver } from '../../util/linkResolver';
import { createRandomId } from '../../util/createRandomId';
import { scrollToTargetElement } from '../../util/scrollToTargetElement';
import { Heading, Button, LazyLink, LazyImage } from '../atoms';

import {
  styledHeroBoxSection,
  styledHeroBoxWrapper,
  styledHeroBoxFullscreen,
  styledHeroBox,
  styledHeroBoxTitle,
  styledButton,
  styledLeftButton,
  StyledFeatureList,
  styledHero,
  StyledHeadings,
  StyledSmallHeading,
  HeroBoxFormContainer,
  StyledForm,
  styledSubmit,
  StyledSubscript,
  StyledButtonsContainer,
  StyledButtonsContainerHr,
  styledHeroBoxForm,
  styledHeroBoxTitleForm,
  styledButtonForm,
  styledButtonWTextForm,
} from './HeroBoxStyles';

const renderHeading = (text, atts) => <Heading {...atts}>{text.text}</Heading>;

const renderBoxFeature = (feature, hero_box_text_color) => {
  const { hero_box_text_icon, hero_box_text } = feature;

  return (
    <li key={createRandomId()}>
      <div>
        <LazyImage alt={hero_box_text_icon.alt || 'text logo'} src={hero_box_text_icon.url} />
        <span style={{ color: hero_box_text_color || '#031b4e' }}>{hero_box_text[0].text}</span>
      </div>
    </li>
  );
};

const HeroBox = ({ input }) => {
  const {
    fullscreen,
    large_text,
    text_color,
    hero_background_color,
    hero_background_image,
    hero_small_heading,
    hero_small_heading_color,
    hero_small_heading_icon,
    hero_heading,
    hero_heading_color,
    hero_subheading,
    hero_subheading_color,
    hero_box_title,
    hero_box_title_color,
    hero_box_cta_text,
    hero_box_cta_url,
    hero_box_text_color,
    hero_box_cta_scroll_to_element_id,
    hero_box_cta_id,
    hero_box_cta_position,
    hero_box_cta_color,
    hero_box_slim,
    email_placeholder,
    form_title,
    name_placeholder,
    password_placeholder,
    submit_button_text,
    subscript,
  } = input.primary;

  const { fields } = input;

  const [currentUrl, setCurrentUrl] = useState('');

  const onButtonClickDroplet = event => {
    const scrollCheck = event.currentTarget.dataset.scroll;
    const getCatType = event.currentTarget.dataset.cat;
    const dataHref = event.currentTarget.dataset.url;
    scrollCheck && scrollToTargetElement(scrollCheck);
    if (getCatType === 'true') {
      window.optimizely.push({
        type: 'event',
        eventName: 'clicked_signup_cta',
        tags: {
          action: 'click',
          category: 'droplet',
          name: 'signup',
        },
      });
      window.analytics.track('Web Interaction', {
        action: 'click',
        category: 'droplet',
        name: 'signup_cta',
      });
    }

    if (dataHref) {
      setTimeout(function() {
        window.location.href = dataHref;
      }, 500);
    }
  };

  const onFormSubmit = event => {
    event.preventDefault();

    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_button',
      tags: {
        action: 'click',
        category: 'form',
        name: 'signup',
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: 'form',
      name: 'signup',
    });

    if (document.getElementById('heroFormPricing')) {
      setTimeout(function() {
        document.getElementById('heroFormPricing').submit();
      }, 500);
    }
  };

  const onButtonClick = event => {
    const clickId = event.currentTarget.id.replace('_button', '');
    const googleIdCheck = clickId === 'Sign up with Google' ? 'google' : clickId;
    const dataHref = event.currentTarget.dataset.href;
    window.optimizely.push({
      type: 'event',
      eventName: 'clicked_signup_button',
      tags: {
        action: 'click',
        category: googleIdCheck,
        name: 'signup',
      },
    });

    window.analytics.track('Web Interaction', {
      action: 'click',
      category: googleIdCheck,
      name: 'signup',
    });

    if (dataHref) {
      setTimeout(function() {
        window.location.href = dataHref;
      }, 500);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !window.document) {
      // return
    } else {
      setCurrentUrl(document.location.pathname === '/products/droplets/');
    }
  }, []);

  return (
    <BulmaHero
      data-testid="bulma_hero"
      css={css`
        ${styledHeroBoxSection}
        ${fullscreen ? styledHeroBoxFullscreen : styledHeroBoxWrapper}
      `}
      style={{
        backgroundColor: hero_background_color || '#031b4e',
        backgroundImage:
          hero_background_image && hero_background_image.url ? `url(${hero_background_image.url})` : null,
      }}
    >
      <BulmaHero.Body>
        <BulmaContainer>
          <BulmaColumns>
            <BulmaColumns.Column css={styledHero} size={7}>
              <StyledHeadings>
                {hero_small_heading && hero_small_heading[0].text && (
                  <StyledSmallHeading>
                    {hero_small_heading_icon && hero_small_heading_icon.url && (
                      <LazyImage alt={hero_small_heading_icon.alt || 'text logo'} src={hero_small_heading_icon.url} />
                    )}
                    {renderHeading(hero_small_heading[0], {
                      style: hero_small_heading_color && { color: hero_small_heading_color },
                      className: text_color === 'light' ? 'h6 white' : 'h6 darkblue',
                      renderAs: 'h6',
                    })}
                  </StyledSmallHeading>
                )}
                {renderHeading(hero_heading[0], {
                  style: hero_heading_color ? { color: hero_heading_color } : null,
                  className: `${text_color === 'light' ? 'h1 white' : 'h1 darkblue'} ${large_text ? 'hero-title' : ''}`,
                  renderAs: 'h1',
                  'data-testid': 'hero_heading',
                })}
                {renderHeading(hero_subheading[0], {
                  style: hero_subheading_color && { color: hero_subheading_color },
                  className: text_color === 'light' ? 'large white' : 'large darkgrey',
                  subtitle: true,
                  renderAs: 'p',
                  'data-testid': 'hero_subheading',
                })}
              </StyledHeadings>
              {hero_box_cta_position === 'left' ? (
                <LazyLink
                  className={`${
                    hero_box_cta_color !== 'white' ? 'button-link is-primary mt-32' : 'button-link is-white mt-32'
                  }`}
                  data-testid="hero_box_cta_button"
                  css={styledLeftButton}
                  url={hero_box_cta_url.url}
                  id={hero_box_cta_id}
                  data-scroll={hero_box_cta_scroll_to_element_id}
                  data-url={hero_box_cta_url.url}
                  data-cat={currentUrl}
                  onClick={onButtonClickDroplet}
                >
                  {hero_box_cta_text[0].text}
                </LazyLink>
              ) : null}
            </BulmaColumns.Column>
            <BulmaColumns.Column size={4}>
              {!form_title ? (
                <BulmaCard css={styledHeroBox} data-testid="hero_box" style={hero_box_slim ? { width: '360px' } : null}>
                  <BulmaCard.Header.Title css={styledHeroBoxTitle}>
                    {renderHeading(hero_box_title[0], {
                      className: 'h5 darkblue',
                      renderAs: 'h5',
                      style: hero_box_title_color ? { color: hero_box_title_color } : null,
                      'data-testid': 'hero_box_title',
                    })}
                  </BulmaCard.Header.Title>
                  <BulmaCard.Content>
                    <StyledFeatureList data-testid="hero_box_feature_list">
                      {input.fields.map(feature => renderBoxFeature(feature, hero_box_text_color))}
                    </StyledFeatureList>
                    {hero_box_cta_position !== 'left' ? (
                      <LazyLink
                        className="button-link is-primary"
                        data-testid="hero_box_cta_button"
                        css={styledButton}
                        url={hero_box_cta_url.url}
                        id={hero_box_cta_id}
                        data-scroll={hero_box_cta_scroll_to_element_id}
                        data-url={hero_box_cta_url.url}
                        data-cat={currentUrl}
                        onClick={onButtonClickDroplet}
                      >
                        {hero_box_cta_text[0].text}
                      </LazyLink>
                    ) : null}
                  </BulmaCard.Content>
                </BulmaCard>
              ) : null}
              {form_title ? (
                <HeroBoxFormContainer>
                  <BulmaCard css={styledHeroBoxForm} data-testid="hero_box">
                    <BulmaCard.Header.Title css={styledHeroBoxTitleForm}>
                      {renderHeading(form_title[0], {
                        className: 'h5 darkblue',
                        renderAs: 'h5',
                        'data-testid': 'hero_box_title',
                      })}
                    </BulmaCard.Header.Title>
                    <BulmaCard.Content>
                      <StyledForm
                        className="form-align-left"
                        action="https://cloud.digitalocean.com/registrations"
                        method="post"
                        id="heroFormPricing"
                        onSubmit={onFormSubmit}
                      >
                        <span className="form-wrapper">
                          <input
                            aria-label="signup button"
                            type="text"
                            minLength="2"
                            name="user[name]"
                            placeholder="First Name"
                            required
                          />
                          <input
                            aria-label="email"
                            type="email"
                            name="user[email]"
                            placeholder="Email Address"
                            required
                          />
                          <input
                            aria-label="password"
                            type="password"
                            minLength="8"
                            name="user[password]"
                            placeholder="Password"
                            required
                          />
                          <Button
                            className="is-primary"
                            css={styledSubmit}
                            type="submit"
                            onClick={onButtonClick}
                            id="form_button"
                          >
                            {submit_button_text}
                          </Button>
                        </span>
                      </StyledForm>
                    </BulmaCard.Content>
                    <StyledButtonsContainerHr />
                    <StyledButtonsContainer>
                      {fields.map(button => (
                        <Button
                          className="is-primary is-outlined"
                          css={button.button_text !== null ? styledButtonWTextForm : styledButtonForm}
                          key={createRandomId()}
                          onClick={onButtonClick}
                          data-href={button.button_link.url}
                          id={button.button_text || 'github_button'}
                          aria-label={button.button_text || 'signup_button'}
                        >
                          <LazyImage src={button.icon.url} alt={button.icon.alt || button.button_text || 'icon'} />
                          {button.button_text}
                        </Button>
                      ))}
                    </StyledButtonsContainer>
                  </BulmaCard>
                  <StyledSubscript>
                    <RichText render={subscript} linkResolver={linkResolver} />
                  </StyledSubscript>
                </HeroBoxFormContainer>
              ) : null}
            </BulmaColumns.Column>
          </BulmaColumns>
        </BulmaContainer>
      </BulmaHero.Body>
    </BulmaHero>
  );
};

export default HeroBox;

HeroBox.propTypes = {
  input: PropTypes.object.isRequired,
};
